import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { WaitingSpinner, GameEndedScreen, LoadingQuestion, TimerBar, getPlayerHeaderLeftButton } from '../shared';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import { getGameName } from '../../utils/gameUtils';
import type { GameStatistics } from '../../types/game';
import '../../App.css';
import '../../styles/game-layout.css';
import '../../styles/player-screen.css';
import { useTranslation } from '../../i18n';

type BreakTheCodeRole = 'breaker' | 'analyst';

interface BreakTheCodeQuestion {
  questionId: string;
  question: string;
  title: string;
  description: string;
  timeLimit: number;
  startTime: number;
  playerId: string;
  role: BreakTheCodeRole;
  teamId: 'A' | 'B';
  canSubmit: boolean;
  clues: Array<{ clue: string; order: number }>;
  codeLength: number;
  breakerName?: string;
  teamLocked?: boolean;
  teamSolved?: boolean;
  questionNumber?: number;
  totalQuestions?: number;
}

interface BreakTheCodePlayerDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string; teamId?: 'A' | 'B' }>;
    teams?: { A: { score: number; name: string }; B: { score: number; name: string } };
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: {
      questionId?: string;
      id?: string;
      question?: string;
      title?: string;
      description?: string;
      timeLimit?: number;
      startTime?: number;
      questionNumber?: number;
      totalQuestions?: number;
      isBreakTheCode?: boolean;
      codeLength?: number;
    };
  };
  socket: WebSocketAdapter;
  myPlayerId: string;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
}

const BreakTheCodePlayerDisplay: React.FC<BreakTheCodePlayerDisplayProps> = ({
  roomState,
  socket,
  myPlayerId,
  onGoHome,
  onLoadingTimeout,
  isHost = false,
  onBackToGameSelection,
}) => {
  const { t } = useTranslation();
  const [btcQuestion, setBtcQuestion] = useState<BreakTheCodeQuestion | null>(null);
  const [codeInput, setCodeInput] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerCorrect, setAnswerCorrect] = useState<boolean | null>(null);
  const [lastPoints, setLastPoints] = useState(0);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [myScore, setMyScore] = useState(0);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const isSubmittingRef = useRef(false);
  const currentQuestionIdRef = useRef<string | undefined>(undefined);
  const myTeamIdRef = useRef<'A' | 'B' | undefined>(undefined);
  const gameEndData = useGameEndScreenData(roomState);

  useEffect(() => {
    if (myPlayerId && roomState.players) {
      const me = roomState.players.find((p) => p.id === myPlayerId);
      if (me) setMyScore(me.score);
    }
  }, [roomState.players, myPlayerId]);

  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setBtcQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    } else if (roomState.gameState === 'playing' && gameEnded) {
      // Host started a new round — leave end screen even if game-started was missed
      setGameEnded(false);
      setBtcQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      setCodeInput('');
      setAnswerSubmitted(false);
      setAnswerCorrect(null);
      currentQuestionIdRef.current = undefined;
    }
  }, [roomState.gameState, gameEndData, gameEnded]);

  useEffect(() => {
    if (!btcQuestion && roomState.currentQuestion?.isBreakTheCode) {
      const q = roomState.currentQuestion;
      const questionId = q.questionId || q.id;
      if (questionId && socket && roomState.roomName) {
        socket.emit('get-break-the-code-state', { roomName: roomState.roomName, questionId });
      }
    }
  }, [roomState.currentQuestion, btcQuestion, socket, roomState.roomName]);

  useEffect(() => {
    const handleQuestion = (data: BreakTheCodeQuestion) => {
      const actualPlayerId = myPlayerId || socket.id;
      if (data.playerId && data.playerId !== actualPlayerId && data.playerId !== socket.id) {
        return;
      }
      currentQuestionIdRef.current = data.questionId;
      myTeamIdRef.current = data.teamId;
      setBtcQuestion(data);
      setCodeInput('');
      setAnswerSubmitted(!!data.teamLocked);
      setAnswerCorrect(data.teamLocked ? !!data.teamSolved : null);
      setTimeRemaining(data.timeLimit);
      isSubmittingRef.current = false;
    };

    const handleAnswerReceived = (data: { questionId: string; isCorrect: boolean }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      setAnswerSubmitted(true);
      setAnswerCorrect(data.isCorrect);
      isSubmittingRef.current = false;
    };

    const handleTeamLocked = (data: {
      questionId: string;
      teamId: 'A' | 'B';
      isCorrect: boolean;
      points?: number;
    }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      if (data.teamId !== myTeamIdRef.current) return;
      setAnswerSubmitted(true);
      setAnswerCorrect(data.isCorrect);
      setLastPoints(data.points ?? 0);
      isSubmittingRef.current = false;
    };

    const handleGameEnded = (data: { leaderboard?: typeof finalLeaderboard; statistics?: GameStatistics }) => {
      setGameEnded(true);
      setBtcQuestion(null);
      setFinalLeaderboard(data.leaderboard || []);
      setGameStatistics(data.statistics || null);
    };

    const handleGameStarted = () => {
      setGameEnded(false);
      setBtcQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      setCodeInput('');
      setAnswerSubmitted(false);
      setAnswerCorrect(null);
      currentQuestionIdRef.current = undefined;
      isSubmittingRef.current = false;
    };

    socket.on('break-the-code-question', handleQuestion);
    socket.on('answer-received', handleAnswerReceived);
    socket.on('break-the-code-team-locked', handleTeamLocked);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);

    return () => {
      socket.off('break-the-code-question', handleQuestion);
      socket.off('answer-received', handleAnswerReceived);
      socket.off('break-the-code-team-locked', handleTeamLocked);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
    };
  }, [socket, myPlayerId]);

  useEffect(() => {
    if (!btcQuestion || answerSubmitted) return;
    const interval = setInterval(() => {
      const elapsed = Date.now() - btcQuestion.startTime;
      const remaining = Math.max(0, btcQuestion.timeLimit - elapsed);
      setTimeRemaining(remaining);
    }, 100);
    return () => clearInterval(interval);
  }, [btcQuestion, answerSubmitted]);

  const handleSubmit = () => {
    if (!btcQuestion || !btcQuestion.canSubmit || answerSubmitted || isSubmittingRef.current) return;
    const digits = codeInput.replace(/\D/g, '');
    if (digits.length !== btcQuestion.codeLength) return;

    isSubmittingRef.current = true;
    socket.emit('submit-answer', {
      roomName: roomState.roomName,
      questionId: btcQuestion.questionId,
      answer: digits,
      timestamp: Date.now(),
    });
  };

  if (gameEnded) {
    const handlePlaySameGame = () => {
      setGameEnded(false);
      setBtcQuestion(null);
      setFinalLeaderboard([]);
      socket.emit('start-game', { roomName: roomState.roomName, gameType: roomState.gameType });
    };

    const teamLeaderboard = roomState.teams
      ? [
          { playerId: 'A', playerName: roomState.teams.A.name, score: roomState.teams.A.score },
          { playerId: 'B', playerName: roomState.teams.B.name, score: roomState.teams.B.score },
        ].sort((a, b) => b.score - a.score)
      : finalLeaderboard.length > 0
        ? finalLeaderboard
        : (gameEndData?.finalLeaderboard ?? []);

    return (
      <GameEndedScreen
        gameType={roomState.gameType}
        finalLeaderboard={teamLeaderboard}
        myPlayerId={myPlayerId}
        myTeamId={myTeamIdRef.current ?? roomState.players.find((p) => p.id === myPlayerId)?.teamId}
        myScore={myScore}
        isHost={isHost}
        onGoHome={onGoHome || (() => {})}
        onPlayAgain={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection}
        showPositionHighlight
        statistics={gameStatistics ?? gameEndData?.gameStatistics ?? undefined}
        feedbackConfig={{ roomName: roomState.roomName, gameType: roomState.gameType, socket }}
      />
    );
  }

  if (!btcQuestion) {
    if (roomState.currentQuestion) {
      return (
        <LoadingQuestion
          socket={socket}
          roomName={roomState.roomName}
          title={t('gameplay.loading.question')}
          onGoHome={onGoHome}
          isHost={isHost}
          onBackToGameSelection={onBackToGameSelection}
          onLongWaitTimeout={onLoadingTimeout}
        />
      );
    }
    return (
      <div className="app">
        <Header
          leftButton={getPlayerHeaderLeftButton({
              isHost,
              onBackToGameSelection,
              onGoHome,
              backTooltip: t('gameplay.backToGameSelection'),
              homeTooltip: t('common.home'),
            })}
          roomCode={roomState.roomName}
        />
        <div className="game-player-wrapper">
          <WaitingSpinner message={t('gameplay.breakTheCode.waiting')} isLoading />
        </div>
      </div>
    );
  }

  const teamScore = roomState.teams?.[btcQuestion.teamId]?.score ?? 0;
  const roleLabel =
    btcQuestion.role === 'breaker'
      ? t('gameplay.breakTheCode.roleBreaker')
      : t('gameplay.breakTheCode.roleAnalyst');
  const gameDisplayName = getGameName(roomState.gameType);

  return (
    <div className="app">
      <Header
        leftButton={getPlayerHeaderLeftButton({
            isHost,
            onBackToGameSelection,
            onGoHome,
            backTooltip: t('gameplay.backToGameSelection'),
            homeTooltip: t('gameplay.leaveGame'),
          })}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage'),
        }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
            <span className="header-game-name" style={{ fontSize: '1rem' }}>{gameDisplayName}</span>
            <span
              style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                padding: '0.3rem 0.6rem',
                backgroundColor: 'rgba(var(--color-primary-rgb), 0.1)',
                borderRadius: '6px',
                border: '2px solid rgba(var(--color-primary-rgb), 0.35)',
                color: 'var(--text-primary)',
              }}
            >
              {roomState.roomName}
            </span>
          </div>
        }
      />

      <div className="game-player-wrapper">
        <div className="container player-content-wrapper" style={{ paddingTop: '3rem', width: '100%', maxWidth: '100%' }}>
          <TimerBar
            timeRemaining={timeRemaining}
            timeLimit={btcQuestion.timeLimit}
            questionNumber={btcQuestion.questionNumber}
            totalQuestions={btcQuestion.totalQuestions}
            showTimeCounter={false}
          />

          <div className="question-container" style={{ marginBottom: '1rem', textAlign: 'center' }}>
            <div
              style={{
                display: 'inline-block',
                marginBottom: '0.75rem',
                padding: '0.25rem 0.75rem',
                borderRadius: '999px',
                fontSize: '0.85rem',
                fontWeight: 700,
                color: 'var(--color-primary)',
                background: 'rgba(var(--color-primary-rgb), 0.12)',
                border: '1px solid rgba(var(--color-primary-rgb), 0.3)',
              }}
            >
              {roleLabel}
            </div>
            <h2 className="title" style={{ fontSize: '1.35rem', color: 'var(--text-primary)', margin: '0 0 0.35rem' }}>
              {btcQuestion.title}
            </h2>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
              {btcQuestion.description}
            </p>
            <p
              style={{
                margin: '0.75rem 0 0',
                fontSize: '0.95rem',
                fontWeight: 700,
                color: 'var(--color-primary)',
              }}
            >
              {t('gameplay.breakTheCode.teamScore', { team: btcQuestion.teamId, score: teamScore })}
            </p>
          </div>

          {btcQuestion.role === 'breaker' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
              <div
                style={{
                  fontSize: '2rem',
                  letterSpacing: '0.5rem',
                  fontFamily: 'monospace',
                  color: 'var(--text-primary)',
                  fontWeight: 700,
                }}
              >
                {'?'.repeat(btcQuestion.codeLength)}
              </div>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={btcQuestion.codeLength}
                value={codeInput}
                disabled={answerSubmitted}
                onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, btcQuestion.codeLength))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit();
                }}
                placeholder={t('gameplay.breakTheCode.enterCode')}
                className="player-surface-panel"
                style={{
                  width: '100%',
                  maxWidth: '280px',
                  padding: '0.85rem',
                  fontSize: '1.4rem',
                  letterSpacing: '0.35rem',
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.08) 0%, rgba(var(--color-secondary-rgb), 0.14) 100%)',
                  border: '3px solid rgba(var(--color-secondary-rgb), 0.28)',
                  borderRadius: '15px',
                  color: 'var(--text-primary)',
                  outline: 'none',
                }}
              />
              <button
                onClick={handleSubmit}
                disabled={answerSubmitted || codeInput.length !== btcQuestion.codeLength}
                className="button player-answer-submit-button"
                style={{ width: '100%', maxWidth: '280px' }}
              >
                {answerSubmitted
                  ? answerCorrect
                    ? t('gameplay.breakTheCode.correct', { points: lastPoints })
                    : t('gameplay.breakTheCode.wrong')
                  : t('gameplay.breakTheCode.submit')}
              </button>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', textAlign: 'center', margin: 0 }}>
                {t('gameplay.breakTheCode.breakerHint')}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {btcQuestion.breakerName && (
                <p style={{ textAlign: 'center', color: 'var(--text-secondary)', margin: '0 0 0.25rem', fontSize: '0.9rem' }}>
                  {t('gameplay.breakTheCode.tellBreaker', { name: btcQuestion.breakerName })}
                </p>
              )}
              {btcQuestion.clues.map((clue, index) => (
                <div
                  key={clue.order}
                  className="collaborative-clue-box"
                  style={{
                    backgroundColor: 'rgba(var(--color-primary-rgb), 0.12)',
                    border: '3px solid var(--color-primary)',
                    borderRadius: '15px',
                    padding: '1.25rem',
                    marginBottom: index < btcQuestion.clues.length - 1 ? '0.25rem' : 0,
                    textAlign: 'center',
                  }}
                >
                  <div
                    className="collaborative-clue-label"
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--color-primary)',
                      marginBottom: '0.6rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {t('gameplay.breakTheCode.clue', { n: clue.order })}
                  </div>
                  <div
                    className="collaborative-clue-text"
                    style={{
                      fontSize: '1.15rem',
                      color: 'var(--text-primary)',
                      fontWeight: 'bold',
                    }}
                  >
                    {clue.clue}
                  </div>
                </div>
              ))}
              {answerSubmitted && (
                <p
                  style={{
                    textAlign: 'center',
                    color: answerCorrect ? 'var(--color-success)' : 'var(--color-error)',
                    fontWeight: 700,
                  }}
                >
                  {answerCorrect ? t('gameplay.breakTheCode.teamSolved', { points: lastPoints }) : t('gameplay.breakTheCode.teamMissed')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BreakTheCodePlayerDisplay;
